import {
  getBalance,
  readContract,
  readContracts,
  writeContract,
  simulateContract,
  waitForTransactionReceipt,
  getPublicClient,
} from '@wagmi/core'
import BigNumber from 'bignumber.js'
import type { Abi } from 'viem'

import Erc20Abi from '@/config/erc20.json'
import XWanFarmingAbi from '@/config/XWanFarming.json'
import XWANSwapAbi from '@/config/XWanToken.abi.json'
import type {
  RewardTokenName,
  Pool,
  GetFormattedTokenBalanceParams,
} from '@/types/xwan-farming-type'
import { configCore, config } from '@/wagmi'

export const readTokenBalance = async ({
  tokenAddress,
  owner,
}: GetFormattedTokenBalanceParams): Promise<string> => {
  const balance = await readContract(config, {
    address: tokenAddress,
    abi: Erc20Abi,
    functionName: 'balanceOf',
    args: [owner],
  })

  return String(balance)
}

// stake xwan
export const readStakeXWANBalance = async (
  addr: `0x${string}`,
  token: `0x${string}`,
) => {
  const res = await getBalance(config, {
    address: addr,
    blockTag: 'latest',
    token,
  })
  return new BigNumber(res.value).toString(10)
}

export const readAllowance = async (
  owner: `0x${string}`,
  spender: `0x${string}`,
  tokenAddress: `0x${string}`,
) => {
  const res = await readContract(config, {
    address: tokenAddress,
    abi: Erc20Abi,
    functionName: 'allowance',
    args: [owner, spender],
  })
  return new BigNumber(String(res)).toString(10)
}

export const writeApprovee = async (
  spender: `0x${string}`,
  amount: string,
  tokenAddress: `0x${string}`,
) => {
  const { request } = await simulateContract(config, {
    address: tokenAddress,
    abi: Erc20Abi,
    functionName: 'approve',
    args: [spender, amount],
  })
  const hash = await writeContract(config, request)
  console.log('request', request, hash)

  await waitForTransactionReceipt(config, {
    hash,
  })
  return hash
}

const handlePoolName = (pool: Pool) => {
  const poolNameArr: RewardTokenName[] = Object.keys(pool) as RewardTokenName[]
  return poolNameArr
}

export const readRewardPoolInfo = async (pool: Pool) => {
  const poolNameArr = handlePoolName(pool)
  const rewardPoolInfoReqArr = poolNameArr.map((v: RewardTokenName) => {
    return {
      address: pool[v].contract as `0x${string}`,
      abi: XWanFarmingAbi as Abi,
      functionName: 'rewardPoolInfo',
      args: [pool[v].rewardAddr],
    }
  })
  const rewardPoolInfoResArr = await readContracts(configCore, {
    contracts: rewardPoolInfoReqArr,
  })
  const rewardPoolInfoResQuerryArr: Array<{
    accRewardPerShare: string
    distributedReward: string
    endTime: string
    lastUpdateTime: string
    refundAddress: `0x${string}`
    rewardPerSecond: string
    rewardToken: `0x${string}`
    startTime: string
    totalReward: string
  }> = []
  if (!rewardPoolInfoResArr.find((v) => v.status !== 'success')) {
    rewardPoolInfoResArr.map((v) => {
      const res = v.result as Array<`0x${string}` | BigInt>
      rewardPoolInfoResQuerryArr.push({
        accRewardPerShare: new BigNumber(res[5].toString()).toString(10),
        distributedReward: new BigNumber(res[7].toString()).toString(10),
        endTime: new BigNumber(res[2].toString()).toString(10),
        lastUpdateTime: new BigNumber(res[4].toString()).toString(10),
        refundAddress: res[8] as `0x${string}`,
        rewardPerSecond: new BigNumber(res[3].toString()).toString(10),
        rewardToken: res[0] as `0x${string}`,
        startTime: new BigNumber(res[1].toString()).toString(10),
        totalReward: new BigNumber(res[6].toString()).toString(10),
      })
      return v
    })
    return rewardPoolInfoResQuerryArr
  } else {
    return []
  }
}

export const readTotalStake = async (pool: Pool) => {
  const poolNameArr = handlePoolName(pool)
  const totalStakedReqArr = poolNameArr.map((v: RewardTokenName) => {
    return {
      address: pool[v].contract as `0x${string}`,
      abi: XWanFarmingAbi as Abi,
      functionName: 'totalStaked',
      args: [],
    }
  })
  const totalStakedResArr = await readContracts(configCore, {
    contracts: totalStakedReqArr,
  })
  const totalStakedResArrQuerryArr: Array<string> = []
  if (!totalStakedResArr.find((v) => v.status !== 'success')) {
    totalStakedResArr.map((v) => {
      const res = v.result as BigInt
      totalStakedResArrQuerryArr.push(new BigNumber(String(res)).toString(10))
      return v
    })
    return totalStakedResArrQuerryArr
  } else {
    return []
  }
}

export const readUserInfo = async (pool: Pool, addr: `0x${string}`) => {
  const poolNameArr = handlePoolName(pool)
  const userInfoReqArr = poolNameArr.map((v: RewardTokenName) => {
    return {
      address: pool[v].contract as `0x${string}`,
      abi: XWanFarmingAbi as Abi,
      functionName: 'userInfo',
      args: [addr],
    }
  })
  const userInfoResArr = await readContracts(configCore, {
    contracts: userInfoReqArr,
  })
  const userInfoArrQuerryArr: Array<{
    amount: string
    lastUpdateTime: string
  }> = []
  if (!userInfoResArr.find((v) => v.status !== 'success')) {
    userInfoResArr.map((v) => {
      const res = v.result as Array<string | BigInt>
      userInfoArrQuerryArr.push({
        amount: new BigNumber(String(res[0])).toString(10),
        lastUpdateTime: new BigNumber(String(res[1])).toString(10),
      })
      return v
    })
    return userInfoArrQuerryArr
  } else {
    return []
  }
}

export const readPendingReward = async (pool: Pool, addr: `0x${string}`) => {
  const poolNameArr = handlePoolName(pool)
  const pendingRewardReqArr = poolNameArr.map((v: RewardTokenName) => {
    return {
      address: pool[v].contract as `0x${string}`,
      abi: XWanFarmingAbi as Abi,
      functionName: 'pendingReward',
      args: [addr, pool[v].rewardAddr],
    }
  })
  const pendingRewardResArr = await readContracts(configCore, {
    contracts: pendingRewardReqArr,
  })
  const pendingRewardArrQuerryArr: Array<string> = []
  if (!pendingRewardResArr.find((v) => v.status !== 'success')) {
    pendingRewardResArr.map((v) => {
      const res = v.result as BigInt
      pendingRewardArrQuerryArr.push(new BigNumber(String(res)).toString(10))
      return v
    })
    return pendingRewardArrQuerryArr
  } else {
    return []
  }
}

export const writeDeposit = async (amount: string, addr: `0x${string}`) => {
  const { request } = await simulateContract(config, {
    abi: XWanFarmingAbi as Abi,
    address: addr,
    functionName: 'deposit',
    args: [BigInt(amount)],
  })
  if (!request.gas) {
    const publicClient = getPublicClient(config)!
    const gasEstimate = await publicClient.estimateContractGas({
      abi: XWanFarmingAbi as Abi,
      address: addr,
      functionName: 'deposit',
      args: [BigInt(amount)],
      account: request.account!,
    })
    request.gas = (gasEstimate * 150n) / 100n
  } else {
    request.gas = (request.gas * 150n) / 100n
  }
  console.log('deposit request gas::', request.gas)

  const hash = await writeContract(config, request)

  await waitForTransactionReceipt(config, {
    hash,
  })
  return hash
}

export const writeWithdraw = async (amount: string, addr: `0x${string}`) => {
  const { request } = await simulateContract(config, {
    abi: XWanFarmingAbi as Abi,
    address: addr,
    functionName: 'withdraw',
    args: [BigInt(amount)],
  })
  if (!request.gas) {
    const publicClient = getPublicClient(config)!
    const gasEstimate = await publicClient.estimateContractGas({
      abi: XWanFarmingAbi as Abi,
      address: addr,
      functionName: 'withdraw',
      args: [BigInt(amount)],
      account: request.account!,
    })
    request.gas = (gasEstimate * 150n) / 100n
  } else {
    request.gas = (request.gas * 150n) / 100n
  }
  const hash = await writeContract(config, request)

  await waitForTransactionReceipt(config, {
    hash,
  })
  return hash
}

export const writeClaim = async (
  tokenAddr: `0x${string}`,
  addr: `0x${string}`,
) => {
  const { request } = await simulateContract(config, {
    abi: XWanFarmingAbi as Abi,
    address: addr,
    functionName: 'claim',
    args: [tokenAddr],
  })
  if (!request.gas) {
    const publicClient = getPublicClient(config)!
    const gasEstimate = await publicClient.estimateContractGas({
      abi: XWanFarmingAbi as Abi,
      address: addr,
      functionName: 'claim',
      args: [tokenAddr],
      account: request.account!,
    })
    request.gas = (gasEstimate * 150n) / 100n
  } else {
    request.gas = (request.gas * 150n) / 100n
  }
  const hash = await writeContract(config, request)

  await waitForTransactionReceipt(config, {
    hash,
  })
  return hash
}

// swap xwan
export const readUserRedeemLength = async (
  addr: `0x${string}`,
  contract: `0x${string}`,
) => {
  const res = await readContract(config, {
    address: contract,
    abi: XWANSwapAbi,
    functionName: 'getUserRedeemLength',
    args: [addr],
  })
  return new BigNumber(new BigNumber(String(res)).toString(10)).toNumber()
}

export const readUserRedeems = async (
  addr: `0x${string}`,
  key: number,
  contract: `0x${string}`,
) => {
  const userRedeemsRes = (await readContract(config, {
    address: contract,
    abi: XWANSwapAbi,
    functionName: 'userRedeems',
    args: [addr, key],
  })) as Array<BigInt>
  if (userRedeemsRes.length > 0) {
    return {
      0: new BigNumber(String(userRedeemsRes[0])).toString(10),
      1: new BigNumber(String(userRedeemsRes[1])).toString(10),
      2: new BigNumber(String(userRedeemsRes[2])).toString(10),
      3: new BigNumber(String(userRedeemsRes[3])).toString(10),
      endTime: new BigNumber(String(userRedeemsRes[3])).toString(10),
      startTime: new BigNumber(String(userRedeemsRes[2])).toString(10),
      wanAmount: new BigNumber(String(userRedeemsRes[1])).toString(10),
      xWanAmount: new BigNumber(String(userRedeemsRes[0])).toString(10),
    }
  } else {
    throw new Error(`get user redeems error, address: ${addr}, key: ${key}`)
  }
}

export const readWanAmountByVestingDuration = async (
  amount: string,
  time: number,
  contract: `0x${string}`,
) => {
  const res = await readContract(config, {
    address: contract,
    abi: XWANSwapAbi,
    functionName: 'getWanAmountByVestingDuration',
    args: [amount, time],
  })
  return new BigNumber(String(res)).toString(10)
}

export const writeSwapDeposit = async (
  amount: string,
  contract: `0x${string}`,
) => {
  const { request } = await simulateContract(config, {
    abi: XWANSwapAbi as Abi,
    address: contract,
    functionName: 'deposit',
    args: [],
    value: BigInt(amount),
  })
  if (!request.gas) {
    const publicClient = getPublicClient(config)!
    const gasEstimate = await publicClient.estimateContractGas({
      abi: XWANSwapAbi as Abi,
      address: contract,
      functionName: 'deposit',
      args: [],
      value: BigInt(amount),
      account: request.account!,
    })
    request.gas = (gasEstimate * 150n) / 100n
  } else {
    request.gas = (request.gas * 150n) / 100n
  }
  const hash = await writeContract(config, request)
  await waitForTransactionReceipt(config, {
    hash,
  })
  return hash
}

export const writeSwapRedeem = async (
  amount: string,
  time: number,
  contract: `0x${string}`,
) => {
  const { request } = await simulateContract(config, {
    abi: XWANSwapAbi as Abi,
    address: contract,
    functionName: 'redeem',
    args: [BigInt(amount), BigInt(time)],
  })
  if (!request.gas) {
    const publicClient = getPublicClient(config)!
    const gasEstimate = await publicClient.estimateContractGas({
      abi: XWANSwapAbi as Abi,
      address: contract,
      functionName: 'redeem',
      args: [BigInt(amount), BigInt(time)],
      account: request.account!,
    })
    request.gas = (gasEstimate * 150n) / 100n
  } else {
    request.gas = (request.gas * 150n) / 100n
  }
  const hash = await writeContract(config, request)
  await waitForTransactionReceipt(config, {
    hash,
  })
  console.log('writeSwapRedeem hash', hash)
  return hash
}

export const writeSwapCancelRedeem = async (
  id: number,
  contract: `0x${string}`,
) => {
  const { request } = await simulateContract(config, {
    abi: XWANSwapAbi as Abi,
    address: contract,
    functionName: 'cancelRedeem',
    args: [BigInt(id)],
  })
  if (!request.gas) {
    const publicClient = getPublicClient(config)!
    const gasEstimate = await publicClient.estimateContractGas({
      abi: XWANSwapAbi as Abi,
      address: contract,
      functionName: 'cancelRedeem',
      args: [BigInt(id)],
      account: request.account!,
    })
    request.gas = (gasEstimate * 150n) / 100n
  } else {
    request.gas = (request.gas * 150n) / 100n
  }
  const hash = await writeContract(config, request)
  await waitForTransactionReceipt(config, {
    hash,
  })
  console.log('writeSwapCancelRedeem hash', hash)
  return hash
}

export const writeSwapFinalizeRedeem = async (
  id: number,
  contract: `0x${string}`,
) => {
  const { request } = await simulateContract(config, {
    abi: XWANSwapAbi as Abi,
    address: contract,
    functionName: 'finalizeRedeem',
    args: [BigInt(id)],
  })
  if (!request.gas) {
    const publicClient = getPublicClient(config)!
    const gasEstimate = await publicClient.estimateContractGas({
      abi: XWANSwapAbi as Abi,
      address: contract,
      functionName: 'finalizeRedeem',
      args: [BigInt(id)],
      account: request.account!,
    })
    request.gas = (gasEstimate * 150n) / 100n
  } else {
    request.gas = (request.gas * 150n) / 100n
  }
  const hash = await writeContract(config, request)
  await waitForTransactionReceipt(config, {
    hash,
  })
  console.log('writeSwapFinalizeRedeem hash', hash)
  return hash
}
